import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import StepperHeader from "../../components/vendors/StepperHeader";

import {
  useVendorExperienceById,
  useCreateVendorExperience,
  useUpdateVendorExperience,
  usePublicLocations,
  usePublicCategories,
} from "../../custom-hooks/queries";

import { FiLoader } from "react-icons/fi";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import StepOne from "../../components/vendors/Step1BasicInfo";

import Step2Availability from "../../components/vendors/Step2MediaAvailability";
import Step4Review from "../../components/vendors/Step3Review";
import Step3Media from "../../components/vendors/Step3Media";

const initialValues = {
  title: "",
  location: null,
  description: "",
  price: "",
  difficulty: "Moderate",
  importantNotes: "",
  category: "",
  categoryId: "",
  images: [],
  availabilityRules: [],
  availabilityOverrides: [],
  durationRules: [],
  addOns: [],
  isPrivate: false,
  maxGuestCount: "",
};

export default function CreateExperience() {
  const [searchParams] = useSearchParams();
  const experienceId = searchParams.get("experienceId");
  const isEditMode = !!experienceId;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: experienceData, isLoading } =
    useVendorExperienceById(experienceId);

  const { data: locationsData } = usePublicLocations();
  const { data: categoriesData } = usePublicCategories();

  const createMutation = useCreateVendorExperience();
  const updateMutation = useUpdateVendorExperience();

  useEffect(() => {
    if (isEditMode && experienceData) {
      const exp = experienceData.data || experienceData;

      const availability = exp.availability?.[0];

      setFormData({
        ...initialValues,

        /* ---------------- STEP 1 ---------------- */
        title: exp.title ?? "",
        description: exp.description ?? "",
        price: exp.price ?? 0,
        difficulty: exp.difficulty ?? "",
        importantNotes: exp.importantNotes ?? "",
        location: exp.location ?? null,
        categoryId: exp.categoryId ?? "",
        category: exp.category ?? null,
        isPrivate: exp.isPrivate ?? false,
        maxGuestCount: exp.maxGuestCount ?? null,
        categoryId: exp.categoryId ?? "",

        images:
          exp.images?.map((url) => ({
            url,
            preview: url,
            isExisting: true,
          })) ?? [],

        /* ---------------- STEP 2 (MAIN FIX) ---------------- */
        baseDurationMinutes: exp.baseDurationMinutes ?? 0,
        baseGroupSize: exp.baseGroupSize ?? 0,
        bufferMinutes: exp.bufferMinutes ?? 0,

        availabilityStartDate: exp.availability?.[0]?.startDate
          ? exp.availability[0].startDate.split("T")[0]
          : null,

        availabilityEndDate: exp.availability?.[0]?.endDate
          ? exp.availability[0].endDate.split("T")[0]
          : null,

        schedules:
          exp.schedules?.map((s) => ({
            id: s.scheduleId,
            name: s.name,
            days: s.openDays || [],
            openTime: s.startTime?.slice(0, 5) || "",
            closeTime: s.endTime?.slice(0, 5) || "",
            isActive: s.isActive === true,
          })) || [],

        escalationRules: exp.escalationRules ?? [],
        availabilityOverrides: exp.availabilityOverrides ?? [],

        /* ---------------- STEP 3 ---------------- */
        addOns: exp.addOns ?? exp.addsOn ?? [],
        status: exp.status ?? "Draft",
        specificStartRequired: exp.specificStartTime || false,
        startInterval: exp.interval || "",
      });
    }
  }, [experienceData, isEditMode]);

  const next = (data) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
    setStep((s) => s + 1);
  };

  const handleStep2Next = (data) => {
    setFormData((prev) => ({
      ...prev,
      ...data,
    }));
    setStep(3);
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const toLocalDateString = (date) => {
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${y}-${m}-${day}`;
  };

  const handleSubmit = (status = "Draft") => {
    setIsSubmitting(true);

    /* 1️⃣ Escalation Rules */
    const mappedEscalationRules =
      formData.escalationRules?.map((r) => ({
        minGuests: Number(r.minGuests),
        maxGuests: Number(r.maxGuests),
        durationMinutes: Number(r.duration),
      })) || [];

    /* 2️⃣ Schedules */
    const mappedSchedules =
      formData.schedules?.map((s) => ({
        name: s.name,
        openDays: s.days,
        startTime: s.openTime,
        endTime: s.closeTime,
        isApplied: !!s.isActive,
      })) || [];

    /* 3️⃣ Availability */
    const availabilityStartDate = formData.rangeStart
      ? toLocalDateString(formData.rangeStart)
      : null;

    const availabilityEndDate = formData.rangeEnd
      ? toLocalDateString(formData.rangeEnd)
      : null;

    /* 4️⃣ Overrides */
    const mappedAvailabilityOverrides = Object.entries(
      formData.calendarAvailability || {},
    )
      .filter(([_, v]) => v?.overrideType)
      .map(([date, v]) => {
        if (v.overrideType === "FULL_CLOSE") {
          return { date, overrideType: "FullDayClosed" };
        }

        if (v.overrideType === "CUSTOM_HOURS") {
          return {
            date,
            overrideType: "CustomWindow",
            startTime: v.customHours?.start,
            endTime: v.customHours?.end,
          };
        }

        if (v.overrideType === "CLOSURE_WINDOWS") {
          return {
            date,
            overrideType: "BlockedRange",
            startTime: v.closures?.[0]?.start,
            endTime: v.closures?.[0]?.end,
          };
        }

        return null;
      })
      .filter(Boolean);

    const payload = {
      title: formData.title,
      description: formData.description,
      price: Number(formData.price),
      difficulty: formData.difficulty,
      importantNotes: formData.importantNotes,
      location: formData.location,
      categoryId: formData.categoryId,

      baseDurationMinutes: Number(formData.baseDuration),
      baseGroupSize: Number(formData.baseGroupSize),
      bufferMinutes: Number(formData.bufferTime),

      isPrivate: formData.isPrivate,
      maxGuestCount: formData.isPrivate ? 0 : Number(formData.maxGuestCount),

      addOns:
        formData.addOns?.map((a) => ({
          name: a.name,
          price: Number(a.price),
        })) || [],

      images: formData.images.map((i) => i.url),

      schedules: mappedSchedules,
      escalationRules: mappedEscalationRules,
      availabilityStartDate,
      availabilityEndDate,
      availabilityOverrides: mappedAvailabilityOverrides,
      status,
      specificStartTime: formData.specificStartRequired || false,
      interval: Number(formData.startInterval) || null,
    };

    const mutation = isEditMode ? updateMutation : createMutation;

    mutation.mutate(isEditMode ? { experienceId, data: payload } : payload, {
      onSuccess: () => {
        toast.success("Experience saved successfully");
        queryClient.invalidateQueries(["vendorExperiences"]);
        navigate("/vendor/experiences");
      },
      onError: () => {
        toast.error("Failed to save experience");
      },
      onSettled: () => {
        // 👇 THIS is important
        setIsSubmitting(false);
      },
    });
  };

  const title = useMemo(() => {
    return [
      "",
      "Basic Information",
      "Availability & Schedule",
      "Media",
      "Review",
    ][step];
  }, [step]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex justify-center py-20">
        <FiLoader className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto py-8">
      <StepperHeader currentStep={step} title={title} />

      {step === 1 && (
        <StepOne
          defaultValues={formData}
          onNext={next}
          categories={categoriesData?.data || []}
          locations={locationsData?.data || []}
        />
      )}

      {step === 2 && (
        <Step2Availability
          defaultValues={formData}
          onNext={handleStep2Next}
          onBack={back}
          isEditMode={isEditMode}
        />
      )}

      {step === 3 && (
        <Step3Media defaultValues={formData} onNext={next} onBack={back} />
      )}

      {step === 4 && (
        <Step4Review
          formData={formData}
          onBack={back}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  );
}
